import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCalendar, FiUser, FiEye, FiHeart, FiShare2, FiArrowRight, FiChevronLeft, FiChevronRight, FiSearch, FiFilter, FiPause, FiPlay } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchField from '../components/SearchField';
import { apiUrls, getHttpHeaders } from '../config/api';
import { stripContentForPreview } from '../utils/blogContentRenderer';

// TypeScript Interfaces
interface BlogsPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface BlogImage {
  id: number;
  image_filename: string;
  image_alt_text?: string;
  sort_order: number;
}

interface Blog {
  id: number;
  title: string;
  description: string;
  author: string;
  status: string;
  image: string;
  seo?: string;
  created_at: string;
  updated_at: string;
  views: number;
  likes: number;
  content_images?: BlogImage[];
}

// CSS Variables
const CSS_VARS = {
  primaryColor: '#253C8C',
  secondaryColor: '#F9D51D',
  textLight: '#718096',
  bgLight: '#f8f9ff',
  shadowLight: '0 4px 20px rgba(37, 60, 140, 0.1)',
  shadowHover: '0 8px 30px rgba(37, 60, 140, 0.2)',
};

const BlogsSection = styled.section`
  padding-top: 120px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
`;

const BlogsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  padding-top: 10px;
`;

const BlogsHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const BlogsTitle = styled.h1`
  font-size: 2.5rem;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 10px;
  font-weight: 700;
`;

const BlogsSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${CSS_VARS.textLight};
  margin-bottom: 30px;
`;

const BlogsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const BlogCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: ${CSS_VARS.shadowLight};
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${CSS_VARS.shadowHover};
  }
`;

const BlogImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  ${BlogCard}:hover & img {
    transform: scale(1.05);
  }
`;

const BlogContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const BlogTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 10px;
  line-height: 1.4;
`;

const BlogDescription = styled.p`
  color: ${CSS_VARS.textLight};
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 15px;
  flex-grow: 1;
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.8rem;
  color: ${CSS_VARS.textLight};
  margin-bottom: 15px;
`;

const BlogActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 15px;
  border-top: 1px solid #e5e7eb;
  margin-top: auto;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${CSS_VARS.textLight};
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;

  &:hover {
    color: ${CSS_VARS.primaryColor};
    background: ${CSS_VARS.bgLight};
  }
`;

const ReadMoreButton = styled.button`
  background: ${CSS_VARS.primaryColor};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: #1e3a8a;
    transform: translateY(-1px);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 50px;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 50px;
  color: red;
`;

const BlogsPage: React.FC<BlogsPageProps> = ({ 
  onHomeClick, 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick, 
  onFaqClick,
  onFeedbackClick,
  onLoginClick, 
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  // Helper function to strip HTML tags for preview
  const stripHtmlTags = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [allBlogs, setAllBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [blogLikes, setBlogLikes] = useState<{[key: number]: number}>({});

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    const updateItemsPerSlide = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setItemsPerSlide(1);
      } else if (width < 1024) {
        setItemsPerSlide(2);
      } else {
        setItemsPerSlide(3);
      }
    };

    updateItemsPerSlide();
    window.addEventListener('resize', updateItemsPerSlide);
    return () => window.removeEventListener('resize', updateItemsPerSlide);
  }, []);

  useEffect(() => {
    setCurrentSlide(0);
  }, [blogs, itemsPerSlide]);

  // Filter blogs based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setBlogs(allBlogs);
    } else {
      const filteredBlogs = allBlogs.filter(blog => 
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.author.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setBlogs(filteredBlogs);
    }
  }, [searchTerm, allBlogs]);

  // Auto-scroll carousel every 30 seconds
  useEffect(() => {
    if (blogs.length <= itemsPerSlide || isCarouselPaused) return; // Don't auto-scroll if paused or all blogs fit in one slide
    
    const interval = setInterval(() => {
      const maxSlides = Math.ceil(blogs.length / itemsPerSlide);
      setCurrentSlide(prev => (prev + 1) % maxSlides);
    }, 30000); // Auto-scroll every 30 seconds

    return () => clearInterval(interval);
  }, [blogs.length, itemsPerSlide, isCarouselPaused]);

  const fetchBlogs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching public blogs...');
      
      const response = await fetch(apiUrls.blogs.list, {
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Public blogs data:', data);
      
      // Handle the new blog service response format
      if (data.blogs && Array.isArray(data.blogs)) {
        // Response has pagination format: { blogs: [...], pagination: {...} }
        setAllBlogs(data.blogs);
        setBlogs(data.blogs);
        
        // Initialize likes state from blog data
        const likesMap: {[key: number]: number} = {};
        data.blogs.forEach((blog: Blog) => {
          likesMap[blog.id] = blog.likes || 0;
        });
        setBlogLikes(likesMap);
      } else if (Array.isArray(data)) {
        // Response is directly an array
        setAllBlogs(data);
        setBlogs(data);
        
        // Initialize likes state from blog data
        const likesMap: {[key: number]: number} = {};
        data.forEach((blog: Blog) => {
          likesMap[blog.id] = blog.likes || 0;
        });
        setBlogLikes(likesMap);
      } else {
        console.warn('Unexpected blog data format:', data);
        setAllBlogs([]);
        setBlogs([]);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs. Please try again.');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBlogClick = (blogId: number): void => {
    console.log('Blog clicked:', blogId);
    // Navigate to blog details or open modal
  };

  const handleLikeClick = async (blogId: number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    console.log('Like clicked for blog:', blogId);
    
    try {
      // Increment like count locally first for immediate feedback
      const currentLikes = blogLikes[blogId] || 0;
      setBlogLikes(prev => ({
        ...prev,
        [blogId]: currentLikes + 1
      }));
      
      // Call API to increment likes on server (using a simple endpoint for now)
      const response = await fetch(`${apiUrls.blogs.list}/${blogId}/like`, {
        method: 'POST',
        headers: getHttpHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Like incremented successfully:', data);
        
        // Update with server response if available
        if (data.likes !== undefined) {
          setBlogLikes(prev => ({
            ...prev,
            [blogId]: data.likes
          }));
        }
      } else {
        console.error('Failed to increment like');
        // Revert the optimistic update if API call failed
        setBlogLikes(prev => ({
          ...prev,
          [blogId]: currentLikes
        }));
      }
    } catch (error) {
      console.error('Error incrementing like:', error);
      // Revert the optimistic update if API call failed
      const currentLikes = blogLikes[blogId] || 0;
      setBlogLikes(prev => ({
        ...prev,
        [blogId]: Math.max(0, currentLikes - 1)
      }));
    }
  };

  const handleShareClick = (blogId: number, e: React.MouseEvent): void => {
    e.stopPropagation();
    console.log('Share clicked for blog:', blogId);
    // Handle share logic
  };

  const handleViewClick = (blogId: number, e: React.MouseEvent): void => {
    e.stopPropagation();
    console.log('View clicked for blog:', blogId);
    // Handle view logic
  };

  // Carousel navigation functions
  const nextSlide = () => {
    const maxSlides = Math.ceil(blogs.length / itemsPerSlide);
    setCurrentSlide(prev => (prev + 1) % maxSlides);
  };

  const prevSlide = () => {
    const maxSlides = Math.ceil(blogs.length / itemsPerSlide);
    setCurrentSlide(prev => (prev - 1 + maxSlides) % maxSlides);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  if (loading) {
    return (
      <BlogsSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <BlogsContainer>
          <LoadingContainer>
            <div style={{ fontSize: '24px', color: CSS_VARS.primaryColor }}>Loading blogs...</div>
          </LoadingContainer>
        </BlogsContainer>
      </BlogsSection>
    );
  }

  if (error) {
    return (
      <BlogsSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <BlogsContainer>
          <ErrorContainer>
            <div style={{ fontSize: '24px' }}>{error}</div>
            <button onClick={fetchBlogs} style={{ marginTop: '20px', padding: '10px 20px' }}>
              Retry
            </button>
          </ErrorContainer>
        </BlogsContainer>
      </BlogsSection>
    );
  }

  return (
    <>
    <BlogsSection>
      <Header 
        onHomeClick={onHomeClick} 
        onBooksClick={onBooksClick} 
        onVideoLearningClick={onVideoLearningClick} 
        onBlogsClick={onBlogsClick} 
        onLoginClick={onLoginClick} 
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
        currentPage="blogs"
      />
      <BlogsContainer>
        <BlogsHeader>
          <BlogsTitle>MathKnots Blog</BlogsTitle>
          <BlogsSubtitle>
            Explore insights, tips, and stories from educators and learners across all subjects.
          </BlogsSubtitle>
        </BlogsHeader>


        {blogs.length > 0 ? (
          <div style={{ position: 'relative', marginBottom: '40px' }}>
            {/* Compact Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #253C8C 0%, #1e40af 100%)',
              borderRadius: '8px',
              padding: '10px 20px',
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 4px 15px rgba(37, 60, 140, 0.15)'
            }}>
              <div>
                <h2 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  margin: 0
                }}>
                  📝 Our Blogs
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  margin: '2px 0 0 0',
                  fontWeight: '500'
                }}>
                  Slide {currentSlide + 1} of {Math.ceil(blogs.length / itemsPerSlide)} • {blogs.length} blogs available
                </p>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {/* Search Field in Banner */}
                <div style={{ minWidth: '300px' }}>
                  <SearchField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search blogs, authors..."
                    isLoading={loading}
                  />
                </div>

                <div style={{
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginRight: '8px',
                  fontWeight: '700'
                }}>
                  {currentSlide + 1} / {Math.ceil(blogs.length / itemsPerSlide)}
                </div>
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'transparent',
                    color: 'white',
                    cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    opacity: currentSlide === 0 ? 0.5 : 1
                  }}
                >
                  <FiChevronLeft size={16} />
                </button>
                
                {/* Pause/Play Button */}
                <button
                  onClick={() => setIsCarouselPaused(!isCarouselPaused)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'transparent',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    marginLeft: '4px',
                    marginRight: '4px'
                  }}
                  title={isCarouselPaused ? 'Play carousel' : 'Pause carousel'}
                >
                  {isCarouselPaused ? <FiPlay size={14} /> : <FiPause size={14} />}
                </button>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide >= Math.ceil(blogs.length / itemsPerSlide) - 1}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'transparent',
                    color: 'white',
                    cursor: currentSlide >= Math.ceil(blogs.length / itemsPerSlide) - 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    opacity: currentSlide >= Math.ceil(blogs.length / itemsPerSlide) - 1 ? 0.5 : 1
                  }}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Carousel Dots */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '20px',
              gap: '8px'
            }}>
              {Array.from({ length: Math.ceil(blogs.length / itemsPerSlide) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: currentSlide === index ? CSS_VARS.primaryColor : '#e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>

            {/* Carousel Container */}
            <div style={{
              overflow: 'hidden',
              borderRadius: '15px'
            }}>
              <div style={{
                display: 'flex',
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 0.5s ease'
              }}>
                {Array.from({ length: Math.ceil(blogs.length / itemsPerSlide) }).map((_, slideIndex) => (
                  <div
                    key={slideIndex}
                    style={{
                      minWidth: '100%',
                      display: 'grid',
                      gridTemplateColumns: `repeat(${itemsPerSlide}, 1fr)`,
                      gap: '30px',
                      padding: '0 10px'
                    }}
                  >
                    {blogs
                      .slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide)
                      .map((blog) => (
                        <BlogCard
                          key={blog.id}
                          onClick={() => handleBlogClick(blog.id)}
                        >
                          <BlogImage>
                            <img 
                              src={blog.image ? `/images/${blog.image}` : '/images/default-blog-thumbnail.jpg'} 
                              alt={blog.title}
                              onError={(e) => {
                                const imgElement = e.target as HTMLImageElement;
                                // Prevent infinite loop - only set fallback once
                                if (!imgElement.dataset.errorHandled) {
                                  console.warn('Failed to load blog image:', blog.image);
                                  imgElement.dataset.errorHandled = 'true';
                                  imgElement.src = '/images/default-blog-thumbnail.jpg';
                                }
                              }}
                            />
                          </BlogImage>
                          
                          <BlogContent>
                            <BlogTitle>{blog.title}</BlogTitle>
                            <BlogDescription>
                              {(() => {
                                const cleanText = stripContentForPreview(blog.description);
                                return cleanText.length > 150 
                                  ? `${cleanText.substring(0, 150)}...`
                                  : cleanText;
                              })()}
                            </BlogDescription>
                            
                            <BlogMeta>
                              <span>
                                <FiUser /> {blog.author}
                              </span>
                              <span>
                                <FiCalendar /> {formatDate(blog.created_at)}
                              </span>
                            </BlogMeta>
                            
                            <BlogActions>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <ActionButton onClick={(e) => handleLikeClick(blog.id, e)}>
                                  <FiHeart />
                                  {blogLikes[blog.id] ? `${blogLikes[blog.id]} Likes` : (blog.likes ? `${blog.likes} Likes` : 'Like')}
                                </ActionButton>
                              </div>
                              
                              <ReadMoreButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('📖 Opening blog detail for:', blog.title);
                                  // Open blog detail in new tab (similar to books)
                                  window.open(`${window.location.origin}?page=blogDetail&id=${blog.id}`, '_blank');
                                }}
                              >
                                Read More
                                <FiArrowRight />
                              </ReadMoreButton>
                            </BlogActions>
                          </BlogContent>
                        </BlogCard>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px',
            color: CSS_VARS.textLight
          }}>
            <FiCalendar size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
            <h3>No blogs found</h3>
            <p>Check back later for new content</p>
          </div>
        )}
      </BlogsContainer>
    </BlogsSection>
    <Footer 
      onBooksClick={onBooksClick}
      onVideoLearningClick={onVideoLearningClick}
      onBlogsClick={onBlogsClick}
      onFaqClick={onFaqClick}
      onFeedbackClick={onFeedbackClick}
      onMkAcademyOnlineClick={onMkAcademyOnlineClick}
    />
    </>
  );
};

export default BlogsPage; 